import React, { useEffect, useMemo, useState } from "react";
import StyledAgGridReact from "../../../kit/StyledAgGridReact";
import StyledGridBox from "../../../kit/GridBox";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTheme } from "@mui/styles";
import { useLocation, useNavigate } from "react-router-dom";
import APP_CONSTANTS from "../../../utils/config";
import { getStatusButton } from "../../../utils/utils";
import { Button } from "@mui/material";
import { useStyles } from "../../../theme/style";
import EditIcon from '@mui/icons-material/Edit';
import { Box } from "@mui/material";
function TableView({ rowData, isAdmin = false, isDevOps = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminPortal = location.pathname.includes("admin");
  const [gridHeight, setGridHeight] = useState("auto");

  const classes = useStyles(theme);

  // Ensure rowData is always an array to prevent AG Grid "rowData.map is not a function" errors
  // This is a safety check that prevents crashes when the parent component passes non-array data
  const safeRowData = Array.isArray(rowData) ? rowData : [];
  
  // Log for debugging when non-array data is received
  if (!Array.isArray(rowData)) {
    console.warn("TableView received non-array rowData:", rowData);
  }

  const calculateGridHeight = () => {
    const screenHeight = window.innerHeight;
    const newHeight = screenHeight - 210;
    setGridHeight(newHeight + "px");
  };

  useEffect(() => {
    calculateGridHeight();
    window.addEventListener("resize", calculateGridHeight);
    return () => {
      window.removeEventListener("resize", calculateGridHeight);
    };
  }, []);

  const textRender = (params) => {
    const { value } = params;
    return <StyledGridBox>{value}</StyledGridBox>;
  };

  const listRender = (params) => {
    const { value } = params;
    return <StyledGridBox>{value.join(", ")}</StyledGridBox>;
  };

  const dateRender = (params) => {
    const { value } = params;
    const date = new Date(value);

    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = date.toLocaleDateString("en-US", options);
    return <StyledGridBox>{formattedDate}</StyledGridBox>;
  };

  // dateRender();
  const RenderStatusButton = (params) => {
    const { value } = params;
    return <>{getStatusButton(value)}</>;
  };

  const RenderPlaygroundButton = (params) => {
    const { data } = params;
    
    const getPlaygroundUrl = () => {
      const currentUrl = window.location.hostname;
      // console.log("currentUrl-----> ", currentUrl);
      if (currentUrl.includes('localhost') || currentUrl.includes('dev')) {
        // Localhost and Dev environment conditions (both use dev playground)
        if (data.app_name === "Admin" && data.request_id === "Admin") {
          return `https://genai-playground-dev.reckitt.com/`;
        } else {
          return `https://genai-playground-dev.reckitt.com?app_name=${data.app_name}&app_id=${data.request_id}`;
        }
      } else {
        // Production/other environment conditions
        if (data.app_name === "Admin" && data.request_id === "Admin") {
          return `https://genai-playground.reckitt.com`;
        } else {
          return `https://genai-playground.reckitt.com?app_name=${data.app_name}&app_id=${data.request_id}`;
        }
      }
    };

    return (
      <div className="playground-button-container">
        {data.status === APP_CONSTANTS.RAPID_STATUS.FINISHED ? (
          <Button
            disableRipple
            className={classes.outlinedButton}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              window.open(
                getPlaygroundUrl(),
                "_blank",
                "noopener,noreferrer"
              );
            }}
            style={{
              fontWeight: theme.fontWeight[500],
              padding: `${theme.size[4]} ${theme.size[8]}`,
            }}
          >
            <span>Prompt Studio</span>
            <OpenInNewIcon
              sx={{
                marginLeft: theme.size[4],
                fontSize: theme.size[16],
              }}
            />
          </Button>
        ) : null}
      </div>
    );
  };

  const commonCellStyle = {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "row",
    alignItems: "center",
  };

  const RenderEditButton = (params) => {
    const { data } = params;
    const status = data?.status;
    const requestId = data?.request_id;

    if (status !== APP_CONSTANTS.RAPID_STATUS.FINISHED) return null;

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!requestId) return;
      navigate(`/rapid/edit-finished-rapidApplication/${requestId}`);
    };

    return (
      <Button
        onClick={handleClick}
        sx={{
          color: theme.palette.primary.main,
          pointerEvents: 'auto',
          minWidth: 40,
          width: 40,
          height: 40,
          borderRadius: "50%",
          // '&:hover': {
          //   color: theme.palette.primary.main,
          //   boxSizing: 'border-box',
          //   border: `1px solid ${theme.palette.primary.main}`,
          //   borderRadius: "50%",
          // },
        }}
      >
        <EditIcon />
      </Button>
    );
  };

  const columnDefs = useMemo(() => {
    if (isAdmin && !isDevOps) {
      return [
        {
          headerName: "Request ID",
          field: "request_id",
          filter: false,
          sortable: true,
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1.8,
        },
        {
          headerName: "Application name",
          field: "app_name",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Project name",
          field: "project_name",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Requested by",
          field: "user_id",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Requested on",
          field: "created_at",
          cellRenderer: dateRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Region",
          field: "region",
          cellRenderer: listRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Status",
          field: "status",
          cellRenderer: RenderStatusButton,
          cellStyle: commonCellStyle,
          flex: 1.5,
        },
        {
          headerName: "",
          field: "playground",
          cellRenderer: RenderPlaygroundButton,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "",
          field: "status",
          cellRenderer: RenderEditButton,
          cellStyle: commonCellStyle,
          flex: 1,
        }
      ];
    } else if (isAdmin && isDevOps) {
      return [
        {
          headerName: "Request ID",
          field: "request_id",
          filter: false,
          sortable: true,
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Application name",
          field: "app_name",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Project name",
          field: "project_name",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Requested by",
          field: "user_id",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Requested on",
          field: "created_at",
          cellRenderer: dateRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Region",
          field: "region",
          cellRenderer: listRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Status",
          field: "status",
          cellRenderer: RenderStatusButton,
          cellStyle: commonCellStyle,
          flex: 2,
        },
        {
          headerName: "Edit",
          field: "status",
          cellRenderer: RenderEditButton,
          cellStyle: commonCellStyle,
          flex: 1,
        }
      ];
    } else if (isAdmin && isDevOps) {
      return [
        {
          headerName: "Request ID",
          field: "request_id",
          filter: false,
          sortable: true,
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Application name",
          field: "app_name",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Project name",
          field: "project_name",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Requested by",
          field: "user_id",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Requested on",
          field: "created_at",
          cellRenderer: dateRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Region",
          field: "region",
          cellRenderer: listRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Status",
          field: "status",
          cellRenderer: RenderStatusButton,
          cellStyle: commonCellStyle,
          flex: 2,
        },
      ];
    } else {
      return [
        {
          headerName: "Request ID",
          field: "request_id",
          filter: false,
          sortable: true,
          // comparator: caseInsensitiveComparator,
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1.8,
          // unSortIcon: true,
        },
        {
          headerName: "Application name",
          field: "app_name",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Project name",
          field: "project_name",
          cellRenderer: textRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Created on",
          field: "created_at",
          cellRenderer: dateRender,
          cellStyle: commonCellStyle,
          flex: 1,
        },
        {
          headerName: "Status",
          field: "status",
          cellRenderer: RenderStatusButton,
          cellStyle: commonCellStyle,
          flex: 2,
        },
        {
          headerName: "",
          field: "playground",
          cellRenderer: RenderPlaygroundButton,
          cellStyle: commonCellStyle,
          flex: 1.5,
        },
        {
          headerName: "",
          field: "status",
          cellRenderer: RenderEditButton,
          cellStyle: commonCellStyle,
          flex: 1,
        }
      ];
    }
  }, [isAdmin, isDevOps]);

  const getRowHeight = () => 60;

  const gridOptions = {
    rowHeight: `${theme.size[100]} !important`,
    suppressRowTransform: true,
    onGridReady: (event) => {
      event.api.sizeColumnsToFit();
      window.onresize = () => {
        event.api.sizeColumnsToFit();
      };
    },
  };

  const filterParams = {
    suppressAndOrCondition: true,
  };

  const defaultColDef = useMemo(() => ({
    filterParams: filterParams,
  }));

  return (
    <div
      className="ag-theme-alpine"
      style={{ height: gridHeight, width: "100%" }}
    >
      <StyledAgGridReact
        rowData={safeRowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        enableSorting={true}
        enableFilter={false}
        gridOptions={gridOptions}
        getRowHeight={getRowHeight}
        onRowClicked={(e) => {
          if (isDevOps) {
            navigate(`/rapid/devops/details/${e.data.request_id}`,{
              state: {
                status: e.data.status,
                appName: e.data.app_name,
              },
            });
            return;
          }
          const status = e.data.status;
          const clickedInsidePlayground = e.event?.target?.closest(
            ".playground-button-container"
          );

          if (clickedInsidePlayground) {
            return; // prevent navigation
          }

          if (
            status ===
              APP_CONSTANTS.RAPID_STATUS.TECHNICAL_OWNER_RESPONSE_PENDING ||
            status === APP_CONSTANTS.RAPID_STATUS.PROCESS_INITIATED
          ) {
            navigate(`/rapid/edit-rapidApplication/${e.data.request_id}`);
          } else if (isAdminPortal) {
            navigate(
              `/rapid/register-rapidApplication/admin/${e.data.request_id}`
            );
          } else {
            navigate(`/rapid/register-rapidApplication/${e.data.request_id}`);
          }
        }}
        getRowStyle={() => ({
          cursor: "pointer", // Apply the pointer cursor to all rows
        })}
      />
    </div>
  );
}

export default TableView;
